import React, { useState, useEffect, useRef } from 'react';
import { Terminal, X, Minimize2, Maximize2, Trash2, Wifi, WifiOff, Download } from 'lucide-react';
import { useWebSocket } from './context/WebSocketContext';
import { fetchParsedDocs, downloadParsedDoc } from './api/compare';

function formatTime(timestamp: string): string {
  const date = new Date(timestamp);
  return date.toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    fractionalSecondDigits: 3,
  } as Intl.DateTimeFormatOptions);
}

const LEVEL_COLOR: Record<string, string> = {
  error: 'text-red-400',
  warning: 'text-yellow-400',
  success: 'text-green-400',
  debug: 'text-gray-400',
};

const LEVEL_BG: Record<string, string> = {
  error: 'bg-red-500/10',
  warning: 'bg-yellow-500/10',
  success: 'bg-green-500/10',
};

export default function DebugConsole() {
  const { isConnected, sessionId, activeSessions, logs, clearLogs } = useWebSocket();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [showAllSessions, setShowAllSessions] = useState(false);
  const logsEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (logsEndRef.current && isOpen && !isMinimized) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, isOpen, isMinimized]);

  const handleDownload = async () => {
    if (!sessionId) return;
    try {
      const docs = await fetchParsedDocs(sessionId);
      for (let i = 0; i < docs.length; i++) {
        const { blob, filename } = await downloadParsedDoc(sessionId, i);
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        if (i < docs.length - 1) await new Promise(r => setTimeout(r, 500));
      }
    } catch {}
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-3 right-3 p-1.5 rounded-md shadow transition-all hover:scale-105 z-50 opacity-40 hover:opacity-100 ${
          isConnected ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-800 hover:bg-gray-700'
        }`}
        title={isConnected ? 'Консоль отладки' : 'Нет подключения'}
      >
        <Terminal className="w-3.5 h-3.5 text-gray-300" />
      </button>
    );
  }

  const filteredLogs = logs.filter(log => {
    if (showAllSessions) return true;
    if (!sessionId) return !log.sessionId;
    if (!log.sessionId) return true;
    return log.sessionId === sessionId;
  });

  return (
    <div className={`fixed bottom-4 right-4 bg-gray-900 rounded-lg shadow-2xl border border-gray-700 z-50 transition-all ${
      isMinimized ? 'w-auto max-w-xs' : 'w-[500px] h-[400px]'
    }`}>
      {/* Header */}
      <div className={`flex items-center justify-between px-3 py-2 bg-gray-800 rounded-t-lg border-b border-gray-700 ${isMinimized ? 'rounded-b-lg' : ''}`}>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Terminal className="w-4 h-4 text-green-400" />
          {!isMinimized && <span className="text-sm font-medium text-gray-200">Debug Console</span>}
          {isConnected ? <Wifi className="w-3 h-3 text-green-400" /> : <WifiOff className="w-3 h-3 text-red-400" />}
          {activeSessions > 0 && (
            <span className="px-1.5 py-0.5 text-xs bg-blue-600 text-white rounded-full">{activeSessions}</span>
          )}
          {!isMinimized && sessionId && (
            <span className="px-2 py-0.5 text-xs bg-green-700 text-white rounded">#{sessionId.slice(0, 8)}</span>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {!isMinimized && (
            <>
              <button
                onClick={() => setShowAllSessions(!showAllSessions)}
                className={`px-2 py-0.5 text-xs rounded transition ${
                  showAllSessions ? 'bg-purple-600 text-white' : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                }`}
              >
                {showAllSessions ? 'Все' : 'Моя'}
              </button>
              <button onClick={handleDownload} className="p-1 text-gray-400 hover:text-green-400 transition" title="Скачать документы">
                <Download className="w-4 h-4" />
              </button>
              <button onClick={clearLogs} className="p-1 text-gray-400 hover:text-white transition" title="Очистить">
                <Trash2 className="w-4 h-4" />
              </button>
            </>
          )}
          <button onClick={() => setIsMinimized(!isMinimized)} className="p-1 text-gray-400 hover:text-white transition">
            {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
          </button>
          <button onClick={() => setIsOpen(false)} className="p-1 text-gray-400 hover:text-red-400 transition">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <div className="h-[calc(100%-40px)] overflow-y-auto p-2 font-mono text-xs">
          {filteredLogs.length === 0 ? (
            <div className="text-gray-500 text-center py-8">Логи появятся здесь при обработке запросов...</div>
          ) : (
            filteredLogs.map(log => (
              <div key={log.id} className={`py-1 px-2 rounded mb-1 ${LEVEL_BG[log.level] || ''}`}>
                <span className="text-gray-500">{formatTime(log.timestamp)}</span>{' '}
                {showAllSessions && log.sessionId && (
                  <span className={`text-xs px-1 rounded mr-1 ${log.sessionId === sessionId ? 'bg-green-800 text-green-300' : 'bg-gray-700 text-gray-400'}`}>
                    {log.sessionId.slice(0, 6)}
                  </span>
                )}
                <span className={LEVEL_COLOR[log.level] || 'text-blue-400'}>{log.message}</span>
                {log.data && <div className="text-gray-500 ml-4 mt-0.5">{JSON.stringify(log.data, null, 2)}</div>}
              </div>
            ))
          )}
          <div ref={logsEndRef} />
        </div>
      )}
    </div>
  );
}
